import React, { useState, useRef, useEffect, useCallback } from "react";
import { cx } from "../../Util/classHelpers";
import { ChDateRangeIcon } from "../Icons/ch-analytics-icons";
import styles from "./dateRangePicker.module.css";

const MAX_RANGE_DAYS = 90;
const TODAY = () => new Date().toISOString().slice(0, 10);

function daysBetween(start, end) {
    if (!start || !end) return 0;
    return Math.round((new Date(end) - new Date(start)) / 86_400_000);
}

function fmtDisplay(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const DateRangePicker = ({ value, onChange, onClear, maxRangeDays = MAX_RANGE_DAYS }) => {
    const [open, setOpen] = useState(false);
    const [draftStart, setDraftStart] = useState(value.start ?? "");
    const [draftEnd, setDraftEnd] = useState(value.end ?? "");
    const ref = useRef(null);
    const today = TODAY();

    const isActive = Boolean(value.start && value.end);

    // sync draft when external value is cleared
    useEffect(() => {
        if (!value.start && !value.end) {
            setDraftStart("");
            setDraftEnd("");
        }
    }, [value.start, value.end]);

    // outside click + Esc
    useEffect(() => {
        function onMouse(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        function onKey(e) {
            if (e.key === "Escape") setOpen(false);
        }
        document.addEventListener("mousedown", onMouse);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onMouse);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    const diff = daysBetween(draftStart, draftEnd);
    const isInverted = draftStart && draftEnd && draftEnd < draftStart;
    const isTooLong = diff > maxRangeDays;
    const isValid = draftStart && draftEnd && !isInverted && !isTooLong;

    const handleApply = useCallback(() => {
        if (!isValid) return;
        onChange({ start: draftStart, end: draftEnd });
        setOpen(false);
    }, [isValid, draftStart, draftEnd, onChange]);

    const handleClear = useCallback(() => {
        setDraftStart("");
        setDraftEnd("");
        onClear();
        setOpen(false);
    }, [onClear]);

    const triggerLabel = isActive
        ? `${fmtDisplay(value.start)} – ${fmtDisplay(value.end)}`
        : "Custom Range";

    return (
        <div className={styles.wrap} ref={ref}>
            <button
                className={cx(styles.trigger, isActive && styles.triggerActive)}
                onClick={() => setOpen((v) => !v)}
                title={isActive ? `${value.start} to ${value.end}` : "Select a custom date range"}
            >
                <ChDateRangeIcon size={13} />
                <span>{triggerLabel}</span>
                {isActive && <span className={styles.activeDot} />}
            </button>

            {open && (
                <div className={styles.panel}>
                    <div className={styles.panelHeader}>Custom date range</div>

                    <div className={styles.inputs}>
                        <label className={styles.inputGroup}>
                            <span className={styles.inputLabel}>Start</span>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={draftStart}
                                max={draftEnd || today}
                                onChange={(e) => setDraftStart(e.target.value)}
                            />
                        </label>
                        <span className={styles.inputSep}>–</span>
                        <label className={styles.inputGroup}>
                            <span className={styles.inputLabel}>End</span>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={draftEnd}
                                min={draftStart || undefined}
                                max={today}
                                onChange={(e) => setDraftEnd(e.target.value)}
                            />
                        </label>
                    </div>

                    {isInverted && (
                        <div className={styles.warning}>
                            End date must be after the start date.
                        </div>
                    )}
                    {isTooLong && !isInverted && (
                        <div className={styles.warning}>
                            Range cannot exceed {maxRangeDays} days (3 months). Please adjust your selection.
                        </div>
                    )}

                    <div className={styles.actions}>
                        <button className={styles.clearBtn} onClick={handleClear}>
                            Clear
                        </button>
                        <button
                            className={styles.applyBtn}
                            onClick={handleApply}
                            disabled={!isValid}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;
